import type { BudgetCategory, BudgetMonth } from './types'
import { stepMonth } from './format'

/**
 * YNAB-style "quick budget" suggestions for a single category.
 *
 * Each shortcut returns the absolute `assigned` value to set, NOT a
 * delta — the UI calls `setAssigned(category, value)` which is the
 * cleaner contract (typing into the cell also sets, not adds).
 *
 * `null` means the suggestion isn't applicable for this category /
 * dataset and should be hidden in the menu.
 */

export interface QuickBudgetSuggestions {
  underfunded: number | null
  // Bring `assigned` up to satisfy the category's monthly goal target.
  // Mirrors YNAB's "Underfunded" shortcut at the goal level (NOT the
  // available level). Returns the absolute new assigned value.
  fundToTarget: number | null
  budgetedLastMonth: number | null
  spentLastMonth: number | null
  avgAssigned3mo: number | null
  avgSpent3mo: number | null
}

/**
 * Suggestions for one category given the current month + the
 * (optionally-loaded) prior 3 months' BudgetMonth docs.
 *
 * The prior-month docs may be undefined when:
 *   - the user hadn't started budgeting that month, OR
 *   - the query is still loading (we don't block on it)
 *
 * Returning `null` for unavailable values keeps the menu honest;
 * showing "Avg Assigned: $0.00" when we just don't know would be a
 * lie.
 */
export function quickBudgetSuggestions(
  category: BudgetCategory,
  priorMonths: Array<BudgetMonth | null | undefined>,
): QuickBudgetSuggestions {
  // "Underfunded" — bring `available` back to 0 by topping up
  // assigned. Available = assigned + activity (+ carryOver), and
  // setting assigned to (assigned - available) lifts available to 0.
  // Only show when currently negative.
  const underfunded =
    category.available < 0 ? Math.max(0, category.assigned - category.available) : null

  // "Fund to goal target" — YNAB's category-level Underfunded shortcut.
  // `targetUnderfunded` is already this-month-pro-rated by YNAB.
  // Suggest assigned += targetUnderfunded. Hide when nothing's owed.
  const fundToTarget =
    category.targetUnderfunded != null && category.targetUnderfunded > 0
      ? round2(category.assigned + category.targetUnderfunded)
      : null

  const priorCats = priorMonths.map((m) => findCategoryIn(m, category.id))

  const last = priorCats[0]
  const budgetedLastMonth = last ? last.assigned : null
  const spentLastMonth = last ? Math.abs(Math.min(0, last.activity)) : null

  const last3Assigned = priorCats.filter((c): c is BudgetCategory => Boolean(c)).slice(0, 3)
  const avgAssigned3mo =
    last3Assigned.length > 0
      ? round2(last3Assigned.reduce((s, c) => s + c.assigned, 0) / last3Assigned.length)
      : null
  const avgSpent3mo =
    last3Assigned.length > 0
      ? round2(
          last3Assigned.reduce((s, c) => s + Math.abs(Math.min(0, c.activity)), 0) /
            last3Assigned.length,
        )
      : null

  return {
    underfunded,
    fundToTarget,
    budgetedLastMonth,
    spentLastMonth,
    avgAssigned3mo,
    avgSpent3mo,
  }
}

function findCategoryIn(month: BudgetMonth | null | undefined, id: string): BudgetCategory | undefined {
  if (!month) return undefined
  for (const g of month.categoryGroups) {
    const c = g.categories.find((c) => c.id === id)
    if (c) return c
  }
  return undefined
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

/** The 3 prior calendar months relative to (year, month), most-recent first. */
export function priorMonthKeys(year: number, month: number, count = 3): Array<{ year: number; month: number }> {
  const out: Array<{ year: number; month: number }> = []
  for (let i = 1; i <= count; i++) {
    out.push(stepMonth(year, month, -i))
  }
  return out
}
